import React, { useEffect, useState } from "react";
import CustomAddModal from "./CustomAddModal";
import { getCargoAiUsageAPI, parseAITextAPI } from "../../containers/User/Calculator/api";
import { toastInfo, toastWarning } from "../../utils";

const MAX_TOKENS = 500;
const MAX_CHARS = 2000; // keep in sync with the backend limit
const DAILY_LIMIT = 3;

// Generic "paste text, AI fills the form" dialog. The caller applies the AI result in onParsed
// (return a string to show it as a warning, throw an Error to show it in the dialog).
export default function CreateWithAIModal({
  open, handleClose, onParsed, kind = "cargo", title = "Create with AI", tips = "", example = "", filledLabel = "Details filled",
}) {
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!open) return;
    getCargoAiUsageAPI(kind).then((res) => setRemaining(res?.data?.remaining)).catch(() => {});
  }, [open, kind]);

  const tokens = Math.ceil(text.length / 4);
  const overLimit = text.length > MAX_CHARS;
  const nearLimit = text.length > MAX_CHARS * 0.8;
  const canGenerate = !loading && !!text.trim() && !overLimit && remaining !== 0;

  const onClose = () => {
    if (loading) return;
    setText("");
    setError("");
    setStatus("");
    handleClose();
  };

  const generate = async () => {
    if (!canGenerate) return;
    if (overLimit) return setError(`Text is too long. Keep it under ${MAX_TOKENS} tokens.`);
    setLoading(true);
    setError("");
    try {
      const res = await parseAITextAPI(text.trim(), kind);
      const warning = await onParsed(res?.data?.result, setStatus, text.trim());
      const left = res?.data?.remaining;
      setRemaining(left);
      if (warning) toastWarning(warning);
      else toastInfo(`${filledLabel}. Please review. ${left ?? ""} AI use(s) left today.`);
      setText("");
      setStatus("");
      handleClose();
    } catch (err) {
      if (err?.status === 429) setRemaining(0);
      setError(err?.data?.message || err?.message || "Something went wrong!");
    } finally {
      setLoading(false);
      setStatus("");
    }
  };

  return (
    <CustomAddModal
      open={open}
      title={title}
      maxWidth="sm"
      saveLabel={loading ? status || "Generating..." : "Generate"}
      saveDisabled={!canGenerate}
      buttonCase="capitalize"
      handleClose={onClose}
      onSubmitConfirm={generate}
    >
      <div className="space-y-1.5">
        <div className="text-[11px] font-medium text-slate-700">
          Paste the details from an email, chat message or notes. AI fills the form. Please review it before saving.
        </div>
        <div className="text-[11px] font-medium text-slate-500">
          {tips}
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === "Enter") generate(); }}
          disabled={loading}
          autoFocus
          rows={8}
          placeholder="Paste cargo details here..."
          className={`w-full px-1.5 py-1 text-[11px] font-medium border rounded-none outline-none resize-none bg-[#ffffd6] focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:bg-slate-100 disabled:cursor-not-allowed ${overLimit ? "border-rose-500" : "border-slate-300"}`}
        />
        <div className="flex items-center justify-between gap-2 text-[11px] font-medium">
          <div className="flex items-center gap-3">
            <span className={overLimit ? "text-rose-600" : nearLimit ? "text-amber-500" : "text-slate-500"}>
              ~{tokens} / {MAX_TOKENS} tokens
            </span>
            {!loading && (text || example) && (
              <button type="button" onClick={() => setText(text ? "" : example)} className="p-0 text-[11px] font-medium text-slate-700 underline bg-transparent border-none cursor-pointer hover:text-slate-900">
                {text ? "Clear" : "Use example"}
              </button>
            )}
          </div>
          <span className={remaining === 0 ? "text-rose-600" : "text-slate-500"}>
            {remaining === null ? `${DAILY_LIMIT} AI uses per day` : `${remaining} of ${DAILY_LIMIT} AI uses left today`}
          </span>
        </div>
        {error && <p className="text-[11px] font-medium text-rose-600" role="alert">{error}</p>}
        {!error && remaining === 0 && <p className="text-[11px] font-medium text-rose-600">Daily AI limit reached. It resets tomorrow (UTC).</p>}
      </div>
    </CustomAddModal>
  );
}
